import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Drawer, Grid, Step, StepButton, Stepper, Typography, Paper, makeStyles } from '@material-ui/core';
import { Android } from '@material-ui/icons';
import { Button, LgSpace, MdSpace, SmSpace } from 'm2m.components';

import { RootState } from 'store';
import { highlightErrors } from 'store/modules/notaComercial';
import { addAlert } from 'store/modules/snackBar';
import { GrupoAcesso, DocumentDownloadInfo, TipoRegistroInstrumentoFinanceiro, BancoResponse, ParticipanteResponse, FuncaoParticipante, RegistroStatus, ReservarUltimoNumeroDaEmissaoRequest, InstrumentoFinanceiro } from 'common/genericTypes';
import { CustomConnector } from 'components/customConnector';
import FormularioCaracteristicas from 'pages/notaComercial/registro/formularioCaracteristicas';
import FormularioCriteriosRemuneracao from 'pages/notaComercial/registro/formularioCriteriosRemuneracao/criteriosRemuneracao';
import FormularioEventos from 'pages/notaComercial/registro/formularioEventosFinanceiros';
import FormularioDocumentos from 'pages/notaComercial/registro/formularioDocumentos';
import { useValidarCampos } from 'pages/notaComercial/registro/useValidarCampos';

import { Finished } from './formularioFinished';
import { FormularioDrawerAditamento } from './formularioDrawerAditamento';
import { FormularioDrawerEdicao } from './formularioDrawerEdicao';
import { EtapaCadastro } from 'common/registro/enums';
import DesminutadorUploader from 'components/DesminutadorUploader/DesminutadorUploader';
import { DivergenciasButton } from 'components/divergenciasButton';

interface Props {
  title: string;
  handleSubmit(): Promise<{ codigo: string; ticket: string } | undefined>;
  handleReturn(): void;
  handleGerarTermoConstitutivo(): Promise<DocumentDownloadInfo>;
  handleSalvarRascunho(): Promise<string>;
  isEdit: boolean;
  codigo?: string;
  notaComercialId?: string;
  instrumentoData?: {
    id: number;
    codigo: string;
    tipoInstrumentoFinanceiro: string;
    origem?: string;
    statusValidacao?: string;
    dataDeCriacao: Date;
  };
  reprocessamentoExecutado?: boolean;
}

const useStyles = makeStyles((theme) => ({
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  preencherButton: {
    background: 'linear-gradient(90deg, rgba(0, 137, 123, 1) 0%, rgba(0, 150, 136, 1) 50%, rgba(38, 166, 154, 1) 100%)',
    marginLeft: theme.spacing(6),
    color: 'white',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(0, 77, 64, 0.3)',
    '&:hover': {
      boxShadow: '0 6px 16px rgba(0, 77, 64, 0.4)',
      transform: 'translateY(-1px)',
    },
  }
}));

const Formulario: React.FC<Props> = (props) => {
  const classes = useStyles();
  const {
    title,
    notaComercialId,
    handleSubmit,
    handleReturn,
    handleGerarTermoConstitutivo,
    handleSalvarRascunho,
    isEdit,
    codigo,
    instrumentoData,
    reprocessamentoExecutado = false,
  } = props;

  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
  const isUsuarioCadastroOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.CadastroOperacoes;
  const isParticipanteCustodiante =
    useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Custodiante;
  const isOperacoesEdit = isUsuarioOperacoes && isEdit;
  const isCustodianteEdit = isParticipanteCustodiante && isEdit;
  const isCadastroOperacoesEdit = isUsuarioCadastroOperacoes && isEdit;

  const typeOfRegister = useSelector((state: RootState) => state.notaComercialCadastro.typeOfRegister);
  const criteriosRemuneracao = useSelector((state: RootState) => state.notaComercialCadastro.criteriosRemuneracao);
  const caracteristicas = useSelector((state: RootState) => state.notaComercialCadastro.caracteristicas);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);
  const getNotaComercialApi = useSelector((state: RootState) => state.notaComercialApi.getNotaComercialDetalhes);

  const [activeStep, setActiveStep] = useState(0);
  const [codigoNotaComercial, setCodigoNotaComercial] = useState<string>('');
  const [ticket, setTicket] = useState<string>('');
  const [finished, setFinished] = useState<boolean>(false);
  const [statusCadastro, setStatusCadastro] = useState<string>();
  const [delimitacaoEfetuada, setDelimitacaoEfetuada] = useState<boolean>(false);
  const [bancos, setBancos] = useState<Array<BancoResponse>>([]);
  const [participantes, setParticipantes] = useState<Array<ParticipanteResponse>>([]);
  const [openConfirmDrawer, setOpenConfirmDrawer] = useState(false);
  const [openConfirmDrawerAditamento, setOpenConfirmDrawerAditamento] = useState(false);
  const [openConfirmDrawerEdicao, setOpenConfirmDrawerEdicao] = useState(false);
  const [isDesminutadorUploaderOpen, setIsDesminutadorUploaderOpen] = useState<boolean>(false);

  const dispatch = useDispatch();
  const steps = [
    '1. Características da Emissão',
    '2. Critérios de Remuneração',
    '3. Eventos Financeiros',
    '4. Documentos',
  ];

  const camposInvalidosCE = useValidarCampos(EtapaCadastro.Caracteristicas);
  const camposInvalidosCR = useValidarCampos(EtapaCadastro.CriteriosRemuneracao);
  const camposInvalidosEF = useValidarCampos(EtapaCadastro.EventosFinanceiros);
  const camposInvalidosDocs = useValidarCampos(EtapaCadastro.Documentos);
  const camposInvalidos = useValidarCampos();

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return <FormularioCaracteristicas
          participantes={participantes}
          bancos={bancos}
          codigo={codigoNotaComercial}
          delimitacaoEfetuada={delimitacaoEfetuada}
        />;
      case 1:
        return <FormularioCriteriosRemuneracao participantes={participantes} bancos={bancos} statusCadastro={statusCadastro} />;
      case 2:
        return <FormularioEventos />;
      case 3:
        return <FormularioDocumentos handleGerarTermoConstitutivo={handleGerarTermoConstitutivo} />;
      default:
        return 'Unknown step';
    }
  }

  const isStepCompleted = (index: number) => {
    switch (index) {
      case 0:
        return camposInvalidosCE.length === 0;
      case 1:
        return camposInvalidosCR.length === 0;
      case 2:
        return camposInvalidosEF.length === 0;
      case 3:
        return camposInvalidosDocs.length === 0;
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (activeStep === steps.length - 1) {
      setOpenConfirmDrawer(true);
      return;
    } else if (isOperacoesEdit || isCustodianteEdit || isCadastroOperacoesEdit) setActiveStep(3);
    else setActiveStep(activeStep + 1);
  };

  const handlePreviousStep = () => {
    if (activeStep === 0) return;
    else setActiveStep(activeStep - 1);
  };

  const handleSubmitInternalBehaviour = async () => {
    try {
      const result = await handleSubmit();
      setCodigoNotaComercial(result?.codigo ?? '');
      setTicket(result?.ticket ?? '');
      setFinished(true);
      setOpenConfirmDrawer(false);
      setOpenConfirmDrawerAditamento(false);
      setOpenConfirmDrawerEdicao(false);
    } catch (error) {
      setFinished(false);
    }
  };

  const validaInvestidores = () => {
    if (criteriosRemuneracao.custodiantes.filter((custodiante) => custodiante.investidoresPrivados.length === 0).length > 0) {
      if (!camposInvalidos.includes('Custodiante sem Investidores')) camposInvalidos.push('Custodiante sem Investidores');
    }
  };

  const handleConcluirCadastro = async () => {
    validaInvestidores();

    if (camposInvalidos.length > 0) {
      dispatch(highlightErrors(true));
      dispatch(
        addAlert({
          message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}.`,
          severity: 'error',
        })
      );
      return;
    }
    setOpenConfirmDrawer(true);
  };

  const handleConcluirAditamento = () => {
    if (camposInvalidos.length > 0) {
      dispatch(highlightErrors(true));
      dispatch(
        addAlert({
          message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}.`,
          severity: 'error',
        })
      );
    } else setOpenConfirmDrawerAditamento(true);
  };

  const handleConcluirEdicao = () => {
    if (camposInvalidos.length > 0) {
      dispatch(highlightErrors(true));
      dispatch(
        addAlert({
          message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}.`,
          severity: 'error',
        })
      );
    } else setOpenConfirmDrawerEdicao(true);
  };

  const finishButtonSwitch = () => {
    switch (typeOfRegister) {
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <Button variant="contained" color="primary" onClick={handleConcluirAditamento} data-testId="btnConcluirAditamentoInstrumento">
            {'Solicitar Aditamento'}
          </Button>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <Button variant="contained" color="primary" onClick={handleConcluirEdicao} data-testId="btnConcluirEdicaoInstrumento">
            {'Concluir Edição'}
          </Button>
        );
      default:
        return (
          <Button variant="contained" color="primary" onClick={handleConcluirCadastro} data-testId="btnConcluirInstrumento">
            {'Concluir'}
          </Button>
        );
    }
  };

  const getParticipantes = useCallback(async () => {
    try {
      const result = await getParticipantesApi(
        dispatch,
        {
          funcoes: [
            FuncaoParticipante.Emissor,
            FuncaoParticipante.Escriturador,
            FuncaoParticipante.Custodiante,
            FuncaoParticipante.Coordenador
          ],
          status: RegistroStatus.Aprovado,
          suspenso: false
        },
        true);
      setParticipantes(result);
    } catch (error) { }
  }, [dispatch]);

  const getNotaComercialById = useCallback(async () => {
    if (notaComercialId) {
      const result = await getNotaComercialApi(dispatch, notaComercialId);
      setStatusCadastro(result.status);

      if (result.coordenadores && result.coordenadores.length > 0) {
        const hasNonNullValue = result.coordenadores.some(c => c.valor !== null);
        setDelimitacaoEfetuada(hasNonNullValue);
      } else {
        setDelimitacaoEfetuada(false);
      }
    }
  }, [dispatch, getNotaComercialApi])

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      setBancos(typeof result === 'string' ? [] : result.banks);
    } catch (error) { }
  }, [dispatch]);

  useEffect(() => {
    getBancos();
  }, [getBancos]);

  useEffect(() => {
    getParticipantes();
  }, [getParticipantes]);

  useEffect(() => {
    if (!statusCadastro) {
      getNotaComercialById();
    }
  }, [getNotaComercialById, statusCadastro])

  useEffect(() => {
    if (codigo) setCodigoNotaComercial(codigo);
  }, [codigo]);

  return (
    <>
      <div className={classes.sectionHeader}>
        <Typography variant="h1">{title}</Typography>
        {isEdit && instrumentoData && (instrumentoData.statusValidacao === 'DIVERGENTE' || instrumentoData.statusValidacao === 'ERRO') && !reprocessamentoExecutado && (
          <DivergenciasButton 
            instrumentoFinanceiro={{
              id: instrumentoData.id,
              codigo: instrumentoData.codigo,
              tipoInstrumentoFinanceiro: instrumentoData.tipoInstrumentoFinanceiro,
              origem: instrumentoData.origem,
              statusValidacao: instrumentoData.statusValidacao,
              dataDeCriacao: instrumentoData.dataDeCriacao
            }}
          />
        )}
        {typeOfRegister !== TipoRegistroInstrumentoFinanceiro.Aditamento && (
          <Button
            variant="contained"
            className={classes.preencherButton}
            onClick={() => setIsDesminutadorUploaderOpen(true)}
            startIcon={<Android />}
          >
            Preencher com IA
          </Button>
        )}
      </div>

      <MdSpace />

      <DesminutadorUploader
        open={isDesminutadorUploaderOpen}
        onClose={() => setIsDesminutadorUploaderOpen(false)}
        maxFileSize={10 * 1024 * 1024}
        title="Preenchimento Inteligente com IA"
        subtitle="Importe sua minuta em PDF e deixe nossa IA preencher automaticamente os campos da escrituração"
        participantes={participantes}
        bancos={bancos}
        instrumentoTipo={InstrumentoFinanceiro.NotaComercial}
      />

      <Stepper
        nonLinear
        activeStep={activeStep}
        connector={<CustomConnector />}
        style={{ padding: 0, display: 'flex', flexWrap: 'wrap' }}
      >
        {steps.map((label, index) => {
          return (
            <Step key={label} onClick={() => setActiveStep(index)}>
              <StepButton completed={isStepCompleted(index)}>{label}</StepButton>
            </Step>
          );
        })}
      </Stepper>
      <MdSpace />

      <div>
        {finished ? (
          <Finished
            setActiveStep={setActiveStep}
            setFinished={setFinished}
            handleReturn={handleReturn}
            codigoNotaComercial={codigoNotaComercial}
            ticket={ticket}
          />
        ) : (
          <>
            {getStepContent(activeStep)}
            <Paper
              style={{
                position: 'fixed',
                bottom: 0,
                width: '100%',
                marginLeft: '-40px',
                backgroundColor: '#fff',
                zIndex: 1,
              }}
            >
              <MdSpace />
              <Grid container justifyContent="center" spacing={8} alignItems="center" style={{ width: '100%' }}>
                <Grid item>
                  <Button variant="outlined" disabled={activeStep === 0} onClick={handlePreviousStep} data-testId="btnPassoAnteriorInstrumento">
                    {'Passo Anterior'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="outlined" disabled={activeStep === steps.length - 1} onClick={handleNextStep} data-testId="btnProximoPassoInstrumento">
                    {'Próximo Passo'}
                  </Button>
                </Grid>
                {!isEdit && (
                  <Grid item>
                    <Button variant="contained" color="secondary" onClick={handleSalvarRascunho} data-testId="btnSalvarRascunhoInstrumento">
                      {'Salvar como Rascunho'}
                    </Button>
                  </Grid>
                )}
                <Grid item>{finishButtonSwitch()}</Grid>
              </Grid>
              <MdSpace />
            </Paper>
            <LgSpace />
          </>
        )}
      </div>
      <Drawer
        anchor="right"
        open={openConfirmDrawer}
        onClose={() => setOpenConfirmDrawer(false)}
        PaperProps={{ style: { padding: '40px', maxWidth: 460 } }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h3">{'Confirmação Emissão'}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <MdSpace />
            <Typography variant="body2">
              {
                'Confirma que este Instrumento Financeiro não está cadastrado em outra depositária de valores mobiliários?'
              }
            </Typography>
            <MdSpace />
            <Button onClick={handleSubmitInternalBehaviour} variant="contained" color="primary" fullWidth>
              {'Sim, prosseguir'}
            </Button>
            <SmSpace />
            <Button onClick={() => setOpenConfirmDrawer(false)} variant="contained" color="error" fullWidth>
              {'Não, cancelar'}
            </Button>
          </Grid>
        </Grid>
      </Drawer>
      <FormularioDrawerAditamento
        open={openConfirmDrawerAditamento}
        setOpen={setOpenConfirmDrawerAditamento}
        handleSubmit={handleSubmitInternalBehaviour}
        codigo={codigoNotaComercial}
      />
      <FormularioDrawerEdicao
        open={openConfirmDrawerEdicao}
        setOpen={setOpenConfirmDrawerEdicao}
        handleSubmit={handleSubmitInternalBehaviour}
        codigo={codigoNotaComercial}
      />
    </>
  );
};

export default Formulario;
